import { type DomainKey } from './domain-assets'

export interface DomainConfig {
	domain: string
	domainKey: DomainKey
	name: string
	siteName: string
	description: string
	supportEmail: string
	/** Transactional email sender address (Resend `from`). Must be a verified
	 * sending domain on the Resend account that maps to this brand's
	 * `domainKey`. See `email.server.ts` for the domainKey → API key mapping. */
	senderEmail: string
	logoType: 'notionicons' | 'framericons'
	proPurchaseUrl: string
	proPrice: string
	proOriginalPrice: string
}

export const DOMAIN_CONFIGS = {
	'notionicons.so': {
		domain: 'notionicons.so',
		domainKey: 'notionicons' as const,
		name: 'Notion Icons',
		siteName: 'Notion Icons',
		description: 'Beautiful icons for your Notion workspace',
		supportEmail: 'hey@notionicons.so',
		senderEmail: 'hello@notionicons.so',
		logoType: 'notionicons' as const,
		proPurchaseUrl:
			'https://framericons.lemonsqueezy.com/buy/59621dbf-49d2-4eb0-9815-765edd0501e1?media=0&logo=0&checkout[discount_code]=EARLY',
		proPrice: '$24.50',
		proOriginalPrice: '$49',
	},
	'app.framericons.com': {
		domain: 'app.framericons.com',
		domainKey: 'framericons' as const,
		name: 'FramerIcons',
		siteName: 'FramerIcons',
		description: 'Premium icon sets for Framer designers',
		supportEmail: 'hey@framericons.com',
		// framericons.com retiring → redirects to iconited.com; emails come
		// from the iconited sender so a stray non-redirected request still works.
		senderEmail: 'hello@updates.iconited.com',
		logoType: 'framericons' as const,
		proPurchaseUrl:
			'https://framericons.lemonsqueezy.com/buy/eedcc8f9-58c2-4837-a4f8-08f2f728375e?checkout[discount_code]=EARLY',
		proPrice: '$24.50',
		proOriginalPrice: '$49',
	},
	'iconited.com': {
		domain: 'iconited.com',
		domainKey: 'iconited' as const,
		name: 'Iconited',
		siteName: 'Iconited',
		description: 'Premium icon sets for designers',
		supportEmail: 'hey@iconited.com',
		senderEmail: 'hello@updates.iconited.com',
		logoType: 'framericons' as const,
		proPurchaseUrl:
			'https://framericons.lemonsqueezy.com/buy/eedcc8f9-58c2-4837-a4f8-08f2f728375e?checkout[discount_code]=EARLY',
		proPrice: '$24.50',
		proOriginalPrice: '$49',
	},
	// Localhost and fallback configuration
	localhost: {
		domain: 'localhost',
		domainKey: 'iconited' as const,
		name: 'Iconited',
		siteName: 'Iconited',
		description: 'Premium icon sets for designers',
		supportEmail: 'hey@iconited.com',
		senderEmail: 'hello@updates.iconited.com',
		logoType: 'framericons' as const,
		proPurchaseUrl:
			'https://framericons.lemonsqueezy.com/buy/eedcc8f9-58c2-4837-a4f8-08f2f728375e?checkout[discount_code]=EARLY',
		proPrice: '$24.50',
		proOriginalPrice: '$49',
	},
} satisfies Record<string, DomainConfig>

export function getDomainConfig(request: Request): DomainConfig {
	const host =
		request.headers.get('X-Forwarded-Host') ??
		request.headers.get('host') ??
		new URL(request.url).host

	// Remove port number if present
	const domain = host.split(':')[0]

	// Check if we have a specific config for this domain
	if (domain && domain in DOMAIN_CONFIGS) {
		return DOMAIN_CONFIGS[domain as keyof typeof DOMAIN_CONFIGS]
	}

	// Check if it's a subdomain of framericons.com
	if (domain && domain.endsWith('.framericons.com')) {
		return DOMAIN_CONFIGS['app.framericons.com']
	}

	// Check if it's a subdomain of iconited.com
	if (domain && domain.endsWith('.iconited.com')) {
		return DOMAIN_CONFIGS['iconited.com']
	}

	// Check if it's a subdomain of notionicons.so
	if (
		domain &&
		(domain.endsWith('.notionicons.so') || domain === 'notionicons.so')
	) {
		return DOMAIN_CONFIGS['notionicons.so']
	}

	// Default to localhost/iconited config
	return DOMAIN_CONFIGS.localhost
}

export function isIconitedDomain(request: Request): boolean {
	return getDomainConfig(request).domainKey === 'iconited'
}

export function getFullDomainUrl(request: Request): string {
	const protocol = request.headers.get('X-Forwarded-Proto') ?? 'http'
	const host =
		request.headers.get('X-Forwarded-Host') ??
		request.headers.get('host') ??
		new URL(request.url).host

	return `${protocol}://${host}`
}
